import { submitReturnRequest } from "@/api/rest/checkoutApi";
import { UserRetailerCheckoutOrder } from "@/components/Checkout/types";
import {
  ResponsiveDialog,
  ShoppingCartItem,
  Button,
  getEntityImage,
  AnalyticsTrackingEvent,
} from "@brandclub/common-ui";

import {
  Box,
  styled,
  Typography,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import React, { useState } from "react";
import { create } from "zustand";
import StoreInput from "../StoreInput";
import { useAppSelector } from "@/redux/hooks";
import { useTrackActions } from "@/utils/hooks/useTracking";

interface ProductReturnDialogState {
  open: boolean;
  close: () => void;
  orderId: string;
  products: ShoppingCartItem[];
  updateOrder: (order: UserRetailerCheckoutOrder) => void;
}

const useProductReturnDialogStore = create<ProductReturnDialogState>((set) => ({
  open: false,
  close: () => set({ open: false }),
  orderId: "",
  products: [],
  updateOrder: (_: UserRetailerCheckoutOrder) => {},
}));

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: "5px 24px 24px 24px",
  color: theme.vars.palette.text.primary,
  ".title": {
    fontSize: 28,
    fontWeight: 600,
    marginBottom: 16,
    [theme.breakpoints.down("md")]: {
      fontSize: 22,
    },
  },
  ".main-content": {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  ".actions": {
    display: "flex",
    width: "100%",
    justifyContent: "flex-end",
    marginTop: 20,
    [theme.breakpoints.down("md")]: {
      justifyContent: "center",
    },
  },
}));

const ProductList = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  ".product-row + .product-row": {
    borderTop: "1px solid #f0f0f0",
  },
}));

const ProductRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  minHeight: 80,
  padding: "12px 0",
  "&:last-child": {
    borderBottom: "none",
  },
}));

const ProductImage = styled("img")(({ theme }) => ({
  width: 64,
  height: 64,
  objectFit: "contain",
  borderRadius: 6,
  background: "#fff",
  [theme.breakpoints.down("md")]: {
    width: 56,
    height: 56,
  },
}));

const ProductInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  flex: 1,
  ".price": {
    fontSize: 15,
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: 14,
    },
  },
  ".name": {
    fontSize: 13,
    fontWeight: 500,
    color: theme.vars.palette.text.primary,
    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    lineHeight: 1.3,
    [theme.breakpoints.down("md")]: {
      WebkitLineClamp: 2,
    },
  },
}));

const ReasonSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,

  maxWidth: "100%",
}));

const REASONS = [
  {
    value: "inaccurate_description",
    displayName: "Inaccurate website description",
  },
  { value: "no_longer_needed", displayName: "No longer needed" },
  { value: "defective", displayName: "Item defective or doesn't work" },
  {
    value: "damaged_shipping_ok",
    displayName: "Product damaged, but shipping box OK",
  },
  { value: "better_price", displayName: "Better price available" },
  { value: "missing_parts", displayName: "Missing or broken parts" },
  {
    value: "both_damaged",
    displayName: "Product and shipping box both damaged",
  },
  {
    value: "extra_item",
    displayName: "Received extra item I didn't buy (no refund needed)",
  },
  { value: "didnt_approve", displayName: "Didn't approve purchase" },
  { value: "wrong_item", displayName: "Wrong item was sent" },
  { value: "bought_mistake", displayName: "Bought by mistake" },
];

export const ProductReturnDialog = () => {
  const state = useProductReturnDialogStore();
  const { open, close, orderId, products } = state;

  const userProfile = useAppSelector((state) => state.userProfile);

  const existingEmail = userProfile?.email;
  const [trackAction] = useTrackActions();

  const [contactEmail, setContactEmail] = useState("");

  // UI state
  const [selectedSkus, setSelectedSkus] = useState<string[]>(
    products.map((p) => p.stacklineSku)
  );
  const [reasonBySku, setReasonBySku] = useState<{ [sku: string]: string }>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleCheck = (sku: string) => {
    setSelectedSkus((prev) =>
      prev.includes(sku) ? prev.filter((s) => s !== sku) : [...prev, sku]
    );
  };

  const handleReasonChange = (sku: string, reason: string) => {
    setReasonBySku((prev) => ({ ...prev, [sku]: reason }));
  };

  const handleSubmit = async () => {
    trackAction(AnalyticsTrackingEvent.CHECKOUT_GUEST_CHECKOUT_REQUEST_RETURN, {
      email: existingEmail ? undefined : contactEmail,
      items: selectedSkus.map((sku) => ({
        stacklineSku: sku,
        retailerSku:
          products.find((p) => p.stacklineSku === sku)?.retailerSku || "",
        returnQuantity:
          products.find((p) => p.stacklineSku === sku)?.quantity || 0,
        returnReason: reasonBySku[sku],
      })),
    });
    const res = await submitReturnRequest({
      orderId: orderId,
      email: existingEmail ? undefined : contactEmail,
      items: selectedSkus.map((sku) => ({
        stacklineSku: sku,
        retailerSku:
          products.find((p) => p.stacklineSku === sku)?.retailerSku || "",
        returnQuantity:
          products.find((p) => p.stacklineSku === sku)?.quantity || 0,
        returnReason: reasonBySku[sku],
        returnReasonDescription: "",
      })),
    });

    const success = res.status === "success";
    const newOrder = res?.userRetailerCheckoutOrder;
    if (success) {
      setIsSubmitted(true);
      state.updateOrder(newOrder);
    } else {
      console.error(res);
    }
  };

  const handleClose = () => {
    // Reset form state when closing
    setIsSubmitted(false);
    setSelectedSkus(products.map((p) => p.stacklineSku));
    setReasonBySku({});
    close();
    setContactEmail("");
  };

  return (
    <ResponsiveDialog
      open={open}
      disableScrollLock
      onClose={handleClose}
      sx={(theme) => ({
        zIndex: 1400,
        "& div.MuiDialog-paper": {
          margin: "0 20px",
          width: "100%",
          maxWidth: 600,
          [theme.breakpoints.down("md")]: {
            maxWidth: "100%",
            margin: "0px 0px",
            padding: "16px 16px",
            boxSizing: "border-box",
          },
        },
      })}
    >
      <Container>
        {isSubmitted ? (
          <>
            <div className="title">Return Submitted</div>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                maxWidth: 350,
                margin: "0 auto",
                padding: "12px 0",
              }}
            >
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ fontSize: 22, marginBottom: 1 }}
              >
                Thank you!
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: 15, color: "#666", lineHeight: 1.4 }}
              >
                We'll email you further instructions within 2-5 business days.
              </Typography>
            </Box>
            <div className="actions">
              <Button
                variant="solid"
                sx={(theme) => ({
                  paddingLeft: "24px",
                  paddingRight: "24px",
                  fontSize: 15,
                  height: 44,
                  fontWeight: 600,
                  [theme.breakpoints.down("md")]: {
                    width: "100%",
                    maxWidth: "280px",
                  },
                })}
                onClick={handleClose}
              >
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="title">Choose items to return</div>
            <div className="main-content">
              <ProductList>
                {products.map((product) => (
                  <Box key={product.stacklineSku} className="product-row">
                    <ProductRow>
                      <Checkbox
                        checked={selectedSkus.includes(product.stacklineSku)}
                        onChange={() => handleCheck(product.stacklineSku)}
                        sx={{ alignSelf: "flex-start", marginTop: 0.5 }}
                      />
                      <ProductImage
                        src={getEntityImage(product.stacklineSku, "product")}
                        alt={product.title || product.stacklineSku}
                      />
                      <ProductInfo>
                        <div className="price">
                          ${product.retailPrice?.toFixed(2)}
                        </div>
                        <div
                          className="name"
                          title={product.title || "Product"}
                        >
                          {product.title || "Product"}
                        </div>
                      </ProductInfo>
                    </ProductRow>
                    {selectedSkus.includes(product.stacklineSku) && (
                      <ReasonSection>
                        <FormControl fullWidth>
                          <InputLabel
                            id={`reason-select-label-${product.stacklineSku}`}
                          >
                            Reason for returning the item
                          </InputLabel>
                          <Select
                            labelId={`reason-select-label-${product.stacklineSku}`}
                            label="Reason for returning the item"
                            value={reasonBySku[product.stacklineSku] || ""}
                            onChange={(e) =>
                              handleReasonChange(
                                product.stacklineSku,
                                e.target.value as string
                              )
                            }
                            MenuProps={{
                              sx: {
                                zIndex: 1500,
                              },
                            }}
                          >
                            {REASONS.map((reason) => (
                              <MenuItem key={reason.value} value={reason.value}>
                                {reason.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </ReasonSection>
                    )}
                  </Box>
                ))}
              </ProductList>
              {existingEmail ? null : (
                <StoreInput
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                  }}
                  label="Contact email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              )}
            </div>
            <div className="actions">
              <Button
                variant="solid"
                sx={(theme) => ({
                  paddingLeft: "24px",
                  paddingRight: "24px",
                  fontSize: 15,
                  height: 44,
                  fontWeight: 600,
                  [theme.breakpoints.down("md")]: {
                    width: "100%",
                    maxWidth: "280px",
                  },
                })}
                disabled={
                  selectedSkus.length === 0 ||
                  selectedSkus.some((sku) => !reasonBySku[sku]) ||
                  (!existingEmail && !contactEmail)
                }
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </div>
          </>
        )}
      </Container>
    </ResponsiveDialog>
  );
};

ProductReturnDialog.show = (
  props: Omit<ProductReturnDialogState, "open" | "close">
) => {
  useProductReturnDialogStore.setState({
    ...props,
    open: true,
  });
};

ProductReturnDialog.hide = () => {
  useProductReturnDialogStore.setState({
    open: false,
  });
};
